import React, { useEffect, useState, useRef } from 'react';
import { Clock, X } from 'lucide-react';
import MessageItem from './MessageItem';
import PatientEncounterForms from './PatientEncounterForms';
import TemplateSelector from './TemplateSelector';
import { usePatientEncounterData } from '../hooks/usePatientEncounterData';
import { useTemplateSelector } from '../hooks/useTemplateSelector';

function MessageList({
  messages,
  isSelectionMode,
  selectedMessages,
  setSelectedMessages,
  captureSelection,
  renderMessageWithViewNoteLinks,
  sendMessage,
  retryMessage,
  messagesEndRef,
  isProcessing,
  onCancelProcessing,
  currentPatient,
  currentEncounter,
  setCurrentPatient,
  setCurrentEncounter,
  fetchNoteData,
  guidedMode
}) {
  const {
    showPatientForm,
    setShowPatientForm,
    showEncounterForm,
    setShowEncounterForm,
    patients,
    setPatients,
    selectedPatientForNote,
    setSelectedPatientForNote,
    templates,
    templatesLoading,
    handleSelectPatient
  } = usePatientEncounterData({ setCurrentPatient });

  const {
    showTemplateSelector,
    setShowTemplateSelector,
    handleTemplateSelect
  } = useTemplateSelector({ sendMessage, currentEncounter, currentPatient });

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === 'assistant' && lastMessage?.suggested_action) {
      const action = lastMessage.suggested_action;
      
      if (action === 'awaiting_encounter_selection') {
        // Only auto-show patient/encounter form when encounter_options
        // dropdown is NOT already present — otherwise the user selects
        // from the dropdown instead.
        if (!lastMessage.encounter_options || lastMessage.encounter_options.length === 0) {
          if (!currentPatient && patients.length > 0) {
            setShowPatientForm(true);
          } else if (currentPatient) {
            setShowEncounterForm(true);
          }
        }
      } else if (action === 'awaiting_template_selection') {
        if (!lastMessage.template_options) {
          setShowTemplateSelector(true);
        }
      }
    } else if (lastMessage?.sender === 'assistant') {
      const hasActiveFormAction = messages.slice(-3).some(
        m => m.sender === 'assistant' &&
        (m.suggested_action === 'awaiting_encounter_selection' ||
         m.suggested_action === 'awaiting_template_selection')
      );
      if (!hasActiveFormAction) {
        setShowPatientForm(false);
        setShowEncounterForm(false);
      }
    }
  }, [messages, currentPatient, patients.length, setShowPatientForm, setShowEncounterForm, setShowTemplateSelector]);

  // Listen for "Write Another Note" button click to open encounter selector
  useEffect(() => {
    const handleOpenEncounterSelector = () => {
      if (patients.length > 0) {
        setShowPatientForm(true);
      }
    };

    const handleOpenPatientForm = () => {
      setShowPatientForm(true);
    };

    window.addEventListener('open_encounter_selector', handleOpenEncounterSelector);
    window.addEventListener('open_patient_form', handleOpenPatientForm);
    return () => {
      window.removeEventListener('open_encounter_selector', handleOpenEncounterSelector);
      window.removeEventListener('open_patient_form', handleOpenPatientForm);
    };
  }, [patients.length, setShowPatientForm]);

  return (
    <div className="flex-1 py-4 space-y-4">
      {messages.map((msg, i) => (
        <MessageItem
          key={i}
          msg={msg}
          index={i}
          messages={messages}
          isSelectionMode={isSelectionMode}
          selectedMessages={selectedMessages}
          setSelectedMessages={setSelectedMessages}
          captureSelection={captureSelection}
          renderMessageWithViewNoteLinks={renderMessageWithViewNoteLinks}
          sendMessage={sendMessage}
          currentPatient={currentPatient}
          currentEncounter={currentEncounter}
          setCurrentPatient={setCurrentPatient}
          setCurrentEncounter={setCurrentEncounter}
          fetchNoteData={fetchNoteData}
          handleSelectPatient={handleSelectPatient}
          guidedMode={guidedMode}
          onRetry={retryMessage}
        />
      ))}
      <div ref={messagesEndRef} />
      
      <PatientEncounterForms
        showPatientForm={showPatientForm}
        setShowPatientForm={setShowPatientForm}
        showEncounterForm={showEncounterForm}
        setShowEncounterForm={setShowEncounterForm}
        selectedPatientForNote={selectedPatientForNote}
        setSelectedPatientForNote={setSelectedPatientForNote}
        setCurrentPatient={setCurrentPatient}
        sendMessage={sendMessage}
        patients={patients}
        setPatients={setPatients}
      />
      
      <TemplateSelector
        showTemplateSelector={showTemplateSelector}
        setShowTemplateSelector={setShowTemplateSelector}
        templates={templates}
        handleTemplateSelect={handleTemplateSelect}
        loading={templatesLoading}
      />

      {isProcessing && (
        <ProcessingIndicator onCancel={onCancelProcessing} />
      )}
    </div>
  );
}

/**
 * Shows elapsed time while the AI is processing, with a visual progress hint.
 * Displays a cancel/timeout warning after 30 seconds.
 */
function ProcessingIndicator({ onCancel }: { onCancel?: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatElapsed = (s: number) => {
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  return (
    <div className="flex items-center justify-center py-3 px-4">
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 italic mt-2">
        <span className="animate-pulse">✍️</span>
        <span>Assistant is thinking...</span>
        <span className="flex items-center gap-1 text-xs font-mono opacity-75">
          <Clock size={12} />
          {formatElapsed(elapsed)}
        </span>
        {elapsed >= 30 && (
          <span className="text-xs text-amber-500 dark:text-amber-400 ml-1">
            Taking longer than usual
          </span>
        )}
        {elapsed >= 10 && onCancel && (
          <button
            onClick={onCancel}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 dark:text-gray-500 dark:hover:text-red-400 ml-2 transition-colors"
            title="Cancel this request"
          >
            <X size={12} />
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export default MessageList;
